import { FormBlock, Toggle } from '@qbe/ads-blocks-forms';
import { FormGroupLabel, TextField, ToggleProps, Typography } from '@qbe/ads-core';
import React, { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { useAppContent } from '~/hooks';

import { ContextualHelpWithButton } from '../../components-ds/ContextualHelp/ContextualHelpWithButton';
import { ExtraInfoFormData } from './types';

const questionOptions = [
  {
    text: 'Yes',
    value: true,
  },
  {
    text: 'No',
    value: false,
  },
];

export const LandlordDetails = () => {
  const { control, watch, unregister } = useFormContext<ExtraInfoFormData>();
  const needLandlordDetails = watch('needLandlordDetails').toString() === 'true';

  useEffect(() => {
    if (!needLandlordDetails) {
      unregister('landlordName');
    }
  }, [needLandlordDetails, unregister]);

  const { getTextContent } = useAppContent({
    basePath: 'dsme.extraInfoMoreBusinessDetailsLandlord',
  });

  return (
    <>
      <FormBlock preset="input" paddingBottom="none">
        {/* Using FormBlock with "input" preset to prevent the helper icon from wrapping to the next line */}
        <Controller
          control={control}
          render={({ field: { ref: _, ...field } }) => (
            <Toggle
              {...field}
              title={getTextContent('fields.needLandlordDetails.title')}
              titleSuffix={
                <ContextualHelpWithButton tracking="help: Do you need to note your landlord on your liability cover?">
                  <Typography>
                    {getTextContent('contextualhelpers.needLandlordDetails.description')}
                  </Typography>
                </ContextualHelpWithButton>
              }
              optionList={questionOptions.map(
                ({ text, value: v }): ToggleProps['optionList'][number] => ({
                  id: `needLandlordDetails_${text}`,
                  label: text,
                  value: v.toString(),
                  defaultChecked: needLandlordDetails === v,
                })
              )}
            />
          )}
          name="needLandlordDetails"
        />
      </FormBlock>

      {needLandlordDetails && (
        <>
          <FormGroupLabel
            alignCenter
            title={getTextContent('fields.landlordName.title')}
            disableBlockPadding
          />
          <FormBlock preset="input">
            <Controller
              control={control}
              render={({ field: { ref: _, ...field }, fieldState }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  label={getTextContent('fields.landlordName.label')}
                  id="landlordName"
                  data-track-value="mask"
                  error={fieldState.invalid}
                  displayIcon
                  helperText={
                    fieldState.invalid
                      ? getTextContent('fields.landlordName.invalidText')
                      : getTextContent('fields.landlordName.helperText')
                  }
                />
              )}
              name="landlordName"
              rules={{
                required: needLandlordDetails,
                maxLength: 34,
              }}
            />
          </FormBlock>
        </>
      )}
    </>
  );
};
